import axios from 'axios';
import Qs from 'qs';

/**
 * API call to accept user's EULA
 *
 * @method acceptEulaAPI
 * @param  {string|number}         userId The new user information
 * @return {Promise}
 */
export const acceptEulaAPI = userId =>
    axios({
        method: 'patch',
        url: `/v2/users/${userId}/terms-accepted`,
    });

/**
 * API call to create user.
 *
 * @method createUserAPI
 * @param  {Object}         userInfo The new user information
 * @return {Promise}
 */
export const createUserAPI = userInfo => {
    const { suppressEmail, setDefaultSubscriptions } = userInfo;
    return axios({
        method: 'post',
        url: '/v2/users',
        data: userInfo,
        params: {
            partnerId: userInfo.partnerId,
            suppressEmail,
            setDefaultSubscriptions,
        },
    });
};

/**
 * API call to fetch info about the given user, via `userId`.
 *
 * @method fetchUserByIdAPI
 * @param  {Object}         userId The user to fetch
 * @return {Promise}
 */
export const fetchUserByIdAPI = userId => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}`,
    });
};

/**
 * API call to fetch preferences for the given user, via `userId`.
 *
 * @method fetchUserPreferencesByIdAPI
 * @param  {Object}         userId The user to fetch
 * @return {Promise}
 */
export const fetchUserPreferencesByIdAPI = userId => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/preferences`,
    });
};

/**
 * API call to update preferences for the given user, via `userId`.
 *
 * @method updateUserPreferencesByIdAPI
 * @param  {Object}         userInfo The current user's information to be updated
 * @return {Promise}
 */
export const updateUserPreferencesByIdAPI = ({ userId, preferences }) => {
    return axios({
        method: 'post',
        data: preferences,
        url: `/v2/users/${userId}/preferences`,
    });
};

/**
 * API call to update user info about the given user via `userId`.
 *
 * @method updateUserByIdAPI
 * @param  {Object}         userInfo The current user's information to be updated
 * @return {Promise}
 */
export const updateUserByIdAPI = userInfo => {
    return axios({
        method: 'put',
        url: `/v2/users/${userInfo.userId}`,
        data: userInfo,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to update user info about the given user via `userId`.
 *
 * @method changeUserStatusAPI
 * @param  {Object}         userInfo The current user's information to be updated
 * @return {Promise}
 */
export const changeUserStatusAPI = userInfo => {
    const { userId, isActive } = userInfo;
    const data = { isActive: isActive ? '1' : '0' };
    return axios({
        method: 'patch',
        url: `/v2/users/${userId}`,
        data,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to import users.
 *
 * @method importUsersAPI
 * @param  {Object}   usersInfo
 * @return {Promise}
 */
export const importUsersAPI = usersInfo => {
    const { partnerId, file, importByPartner } = usersInfo;
    return axios({
        method: 'post',
        url: `/v2/users/bulk-async?partnerId=${partnerId}&importByPartner=${importByPartner}&suppressEmail=true`,
        data: file,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

/**
 * API call list users by current user logged in.
 *
 * @method fetchUsersAPI
 * @param  {Object}         data
 * @return {Promise}
 */
export const fetchUsersAPI = data => {
    return axios({
        method: 'get',
        url: '/v2/users',
        params: data,
        paramsSerializer(params) {
            return Qs.stringify(params, { arrayFormat: 'repeat' });
        },
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

/**
 * API to update user terms and conditions status
 *
 * @method  updateUserTermsAndConditionsStatus
 * @param   {Object}        userInfo
 * @return  {Promise}
 */
export const updateUserTermsAndConditionsStatus = ({ userId }) =>
    axios({
        method: 'put',
        url: `/v2/users/terms_accepted/${userId}`,
    });

/**
 * API call to download csv template to import users.
 *
 * @method downloadCsvUserTemplateAPI
 * @param  {Object}         partnerId
 * @return {Promise}
 */
export const downloadCsvUserTemplateAPI = partnerId => {
    return axios({
        method: 'get',
        url: `/v2/users/csv?type=template&partnerId=${partnerId}`,
    });
};

/**
 * API to fetch a preview of a user's email history
 *
 * @method fetchUserEmailHistoryPreviewAPI
 * @param  {string} userEmail
 * @param  {string/Number} userId
 * @return {Promise}
 */
export const fetchUserEmailHistoryPreviewAPI = ({ email, userId }) =>
    axios({
        method: 'get',
        url: `/v2/email-history?recipient=${email}&sort=createdOn&limit=15&userId=${userId}`,
    });

/**
 * API to export users CSV.
 *
 * @method exportUsersCSV
 * @param  {Object}     params
 * @return {Promise}
 */
export const exportUsersCSV = params =>
    axios({
        method: 'get',
        url: '/v2/users',
        params,
        paramsSerializer(params) {
            return Qs.stringify(params, { arrayFormat: 'repeat' });
        },
        headers: {
            Accept: 'text/csv',
        },
        responseType: 'blob',
    });

/**
 * API to fetch dashboard widgets for a user
 *
 * @method fetchDashboardWidgetsAPI
 * @param  {number|string}     userId
 * @return {Promise}
 */
export const fetchDashboardWidgetsAPI = userId =>
    axios({
        method: 'get',
        url: `/v2/users/${userId}/widgets`,
    });

/**
 * API to update dashboard widgets for a user
 *
 * @method updateDashboardWidgetsAPI
 * @param  {number|string}     userId
 * @param  {Array}             widgets
 * @return {Promise}
 */
export const updateDashboardWidgetsAPI = (userId, widgets) =>
    axios({
        data: {
            widgets,
        },
        method: 'put',
        url: `/v2/users/${userId}/widgets`,
    });

/**
 * API to check subscription status for a user
 *
 * @method checkUserSubscriptionStatusAPI
 * @param  {string}     token
 * @param  {string}     email
 * @param  {boolean}     isProspect
 * @return {Promise}
 */
export const checkUserSubscriptionStatusAPI = ({ token, isProspect, email }) =>
    axios({
        ignoreAuthorization: true,
        method: 'get',
        params: {
            token,
            isProspect,
            email,
        },
        preventRefreshToken: true,
        url: '/v2/user-preferences/unsubscribe',
    });

/**
 * API to unsubscribe user from subscriptions
 *
 * @method unsubscribeUserFromSubscriptionsAPI
 * @param  {string}     token
 * @param  {string}     isProspect
 * @param  {string}     email
 * @return {Promise}
 */
export const unsubscribeUserFromSubscriptionsAPI = ({ isProspect, token, email }) =>
    axios({
        ignoreAuthorization: true,
        method: 'patch',
        params: {
            token,
            isProspect,
            email,
        },
        preventRefreshToken: true,
        url: '/v2/user-preferences/unsubscribe',
    });

/**
 * API to bulk user subscriptions
 *
 * @method bulkUserSubscriptionsAPI
 * @param  {Object}     payload
 * @return {Promise}
 */
export const bulkUserSubscriptionsAPI = payload => {
    const { partnerId, ...data } = payload;
    return axios({
        method: 'post',
        url: '/v2/user-preferences/bulk',
        params: { partnerId },
        data,
    });
};

/**
 * API call to update partner configurations.
 *
 * @method updatePartnerConfigurationsAPI
 * @param  {string|number}         userId The new user information
 * @return {Promise}
 */
export const updatePartnerConfigurationsAPI = ({ partnerId }) =>
    axios({
        method: 'post',
        url: `/v2/partner-configurations/${partnerId}/accept-banner-ads-tos?bannerAdsTOSAccepted=true`,
    });

/**
 * API call to update additional info about the given user, via `userInfo`.
 *
 * @method updateUsersAdditionalDataAPI
 * @param  {Object}         userInfo The user to fetch
 * @return {Promise}
 */
export const updateUsersAdditionalDataAPI = userInfo => {
    const { userId, id, ...rest } = userInfo;
    return axios({
        method: 'put',
        url: `/v2/users/${userId}/additional-data/${id}`,
        data: rest,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to create additional info about the given user, via `userInfo`.
 *
 * @method createUsersAdditionalDataAPI
 * @param  {Object}         userInfo The user to fetch
 * @return {Promise}
 */
export const createUsersAdditionalDataAPI = userInfo => {
    const { userId, ...rest } = userInfo;
    return axios({
        method: 'post',
        url: `/v2/users/${userId}/additional-data`,
        data: rest,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * API call to fetch additional info about the given user, via `userInfo`.
 *
 * @method fetchUsersAdditionalDataAPI
 * @param  {Object}         userInfo The user to fetch
 * @return {Promise}
 */
export const fetchUsersAdditionalDataAPI = userInfo => {
    const { userId, id } = userInfo;
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/additional-data/${id}`,
    });
};

/**
 * API call to get additional info about the given user, via `userInfo`.
 *
 * @method getUsersAdditionalDataAPI
 * @param  {Object}         userInfo The user to fetch
 * @return {Promise}
 */
export const getUsersAdditionalDataAPI = userInfo => {
    const { userId } = userInfo;
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/additional-data`,
    });
};

/**
 * API call to get user settings for the given user, via `userId`.
 * @method getUserSettingsAPI
 * @param  {number|string} userId
 * @return {Promise}
 */

export const getUserSettingsAPI = userId => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/settings`,
    });
};

/**
 * API call to update user settings for the given user, via `userId`.
 * @method updateUserSettingsAPI
 * @param {object} payload
 * @return {Promise}
 */

export const updateUserSettingsAPI = payload => {
    const { userId, signInMethod, params } = payload;
    return axios({
        method: 'put',
        url: `/v2/users/${userId}/settings?signInMethod=${signInMethod}`,
        params,
    });
};

/**
 * API call to fetch user's PPA enablement status.
 * @method getPPAStatusAPI
 * @param  {string|number} userId The ID of the user whose PPA status is being queried
 * @return {Promise}
 */
export const getPPAStatusAPI = userId => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/settings`,
    });
};

/**
 * API call to set user's PPA status and company assignments.
 * @method setPPAandCompanyAssignmentsAPI
 * @param  {string|number} userId The ID of the user whose PPA status is being queried
 * @param  {Object} assignmentInfo The PPA status and company assignments to be set
 * @return {Promise}
 */
export const setPPAandCompanyAssignmentsAPI = ({ userId, assignmentInfo }) => {
    return axios({
        method: 'patch',
        url: `/v2/users/${userId}/company-assignments`,
        data: assignmentInfo,
    });
};

/**
 * API call to fetch user's company assignments.
 * @method getUserCompanyAssignmentsAPI
 * @param  {string|number} userId The ID of the user whose company assignments are being queried
 * @param  {Object} params Additional parameters for the API call
 * @return {Promise}
 */

export const getUserCompanyAssignmentsAPI = ({ userId, params }) => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/company-assignments`,
        params,
    });
};

/**
 * API call to resend verification email.
 *
 * @method resendVerificationEmailAPI
 * @param  {number|string}      userId
 * @return {Promise}
 * */
export const resendVerificationEmailAPI = ({ userId }) => {
    return axios({
        method: 'post',
        url: `/v2/users/${userId}/settings/resend-verification`,
    });
};

/**
 * API call to cancel verification email.
 *
 * @method cancelVerificationEmailAPI
 * @param  {number|string}      userId
 * @return {Promise}
 */
export const cancelVerificationEmailAPI = ({ userId }) => {
    return axios({
        method: 'patch',
        url: `/v2/users/${userId}/settings/cancel-verification-email-request`,
    });
};

export const emailUpdateVerificationAPI = ({ verificationCode }) => {
    return axios({
        method: 'post',
        url: `/v2/users/email-update-verification?verificationCode=${verificationCode}`,
    });
};

/**
 * API call to download csv template for Company Assignments.
 *
 * @method companyAssignmentCSVDownloadTemplateAPI
 * @param  {Object}         userId
 * @return {Promise}
 */
export const companyAssignmentCSVDownloadTemplateAPI = userId => {
    return axios({
        method: 'get',
        url: `/v2/users/${userId}/company-assignments/download-template-csv?type=template`,
    });
};

/**
 * API call to import Company Assignments.
 *
 * @method importCompanyAssignmentsAPI
 * @param  {Object}   assignmentsInfo
 * @return {Promise}
 */
export const importCompanyAssignmentsAPI = assignmentsInfo => {
    const { userId, file } = assignmentsInfo;
    return axios({
        method: 'post',
        url: `/v2/users/${userId}/company-assignments/import-company-assignments`,
        data: file,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};
